/**
 * Categorical colour rule (design-reference §7).
 *
 * A document type OWNS its colour for the whole app: the colour lives on the
 * category, not on a render-time series index, so a chart that filters or
 * reorders categories still shows the same hue for the same type. Reimplemented
 * as a name→token lookup, exactly as §7.2 requires ("never palette[index]").
 *
 * The map seeds the seven base categories from the mock; a custom category
 * created in-session (or returned by the taxonomy API) is assigned the next free
 * token deterministically and remembered, so its colour is stable across charts.
 */

/**
 * The catch-all the pipeline files a document under when no type could be
 * determined (the backend's `OTHER_CATEGORY_NAME`).
 *
 * It is a routing sentinel, **not** a real category: it is never seeded into a
 * tenant's taxonomy, so it must never be offered as a suggestion to accept or
 * as a category to create — `isProposedNew` would otherwise treat it as a
 * brand-new type simply because it is absent from the taxonomy.
 */
export const OTHER_CATEGORY_NAME = 'Other / Unclassified';

/** The seven categorical tokens, in palette order. */
export const CATEGORICAL_TOKENS = [
  'var(--c1)',
  'var(--c2)',
  'var(--c3)',
  'var(--c4)',
  'var(--c5)',
  'var(--c6)',
  'var(--c7)',
] as const;

/** Mix `token` with the next token round the wheel, `weight`% of the original. */
function blend(index: number, weight: number): string {
  const token = CATEGORICAL_TOKENS[index] ?? CATEGORICAL_TOKENS[0];
  const next = CATEGORICAL_TOKENS[(index + 1) % CATEGORICAL_TOKENS.length] ?? token;
  return `color-mix(in oklab, ${token} ${String(weight)}%, ${next})`;
}

/**
 * Twenty-one hues: the seven tokens, then two interpolated steps between each
 * neighbouring pair.
 *
 * Seven is enough for a by-source donut and far too few for a taxonomy — at 41
 * document types every colour came round six times, so a wedge could not be
 * matched to its legend row at all. Extra hues rather than extra shades because a
 * hue is what makes two wedges tell apart; interpolating *between* adjacent
 * tokens keeps every one of them inside the palette's own range, which a set of
 * invented colours would not.
 *
 * The seven tokens come first, so a category that already had a colour keeps it —
 * a type owns its colour app-wide (design-reference §7.2).
 */
const HUES: readonly string[] = [
  ...CATEGORICAL_TOKENS,
  ...CATEGORICAL_TOKENS.map((_, i) => blend(i, 67)),
  ...CATEGORICAL_TOKENS.map((_, i) => blend(i, 33)),
];

/**
 * Two treatments per hue: as it comes, and deepened toward the ink.
 *
 * There is deliberately **no** lighter tier. Pulling a hue toward `--card` was the
 * obvious way to double the palette and it looked washed out — a third of a
 * 41-type pie rendered as pale pastels that read as "disabled" rather than as
 * distinct categories. Deepening keeps the colour saturated, so both tiers look
 * like deliberate colours.
 *
 * Expressed against a **theme token** rather than black, so it flips with the
 * theme: `--t1` is near-black in light mode and near-white in dark, so this always
 * means "further from the background", never "darker" in the absolute.
 */
const TREATMENTS: readonly ((hue: string) => string)[] = [
  (hue) => hue,
  (hue) => `color-mix(in oklab, ${hue} 72%, var(--t1))`,
];

/**
 * 42 distinct categorical colours — enough for the 40-entry base taxonomy plus
 * the catch-all and a tenant's own additions.
 *
 * Hue cycles fastest, so consecutively-assigned categories differ in hue rather
 * than in shade: neighbouring wedges are the pairs a reader most needs to tell
 * apart, and only the last 20 of 41 reach the deepened tier at all.
 */
export const CATEGORICAL_SEQUENCE: readonly string[] = TREATMENTS.flatMap((treat) =>
  HUES.map((hue) => treat(hue)),
);

const BASE: Record<string, string> = {
  Invoice: 'var(--c1)',
  'Purchase Order': 'var(--c2)',
  'Delivery Note': 'var(--c3)',
  Contract: 'var(--c4)',
  'HR Form': 'var(--c5)',
  'Remittance Advice': 'var(--c6)',
  Other: 'var(--c7)',
};

/**
 * A colour registry. Seeded with the base taxonomy; hands stable colours to
 * new categories. Constructed fresh where isolation matters (tests, tenants).
 */
export class CategoryPalette {
  private readonly assigned = new Map<string, string>();
  private cursor = 0;

  constructor(seed: Record<string, string> = BASE) {
    for (const [name, token] of Object.entries(seed)) {
      this.assigned.set(name, token);
    }
  }

  /** The stable colour token for `name`, assigning one on first sight. */
  colorFor(name: string): string {
    const existing = this.assigned.get(name);
    if (existing !== undefined) return existing;
    // Prefer a colour not already handed out — across all 42, not just the seven
    // base tokens, or a 41-type taxonomy exhausts the palette after seven and
    // every type from the eighth on shares with an earlier one. Falls back to a
    // stable cyclic pick once even 42 are spoken for.
    const used = new Set(this.assigned.values());
    const free = CATEGORICAL_SEQUENCE.find((colour) => !used.has(colour));
    const cyclic =
      CATEGORICAL_SEQUENCE[this.cursor % CATEGORICAL_SEQUENCE.length] ?? CATEGORICAL_TOKENS[0];
    const token = free ?? cyclic;
    this.cursor += 1;
    this.assigned.set(name, token);
    return token;
  }
}

/** The app-wide default palette (base taxonomy). */
export const defaultPalette = new CategoryPalette();

/** Convenience: the stable colour token for a category name. */
export function categoryColor(name: string): string {
  return defaultPalette.colorFor(name);
}
